import axios, { AxiosError } from "axios";
import {
  IconUpdateFlag,
  GetUserProfileResponse,
  GetUsersResponse,
  UpdateUserProfileRequest,
  UpdateUserProfileResponse,
  UpdateCorporateUserProfileRequest,
  CreatePaymentInformationRequest,
  ChangePasswordRequest,
  ChangeMailAddressRequest,
  RegisterPhoneNumberRequest,
  GetAuthPhoneNumberRequest,
  GetAuthPhoneNumberResponse,
  SendAuthCode,
  SendMailAuthCode,
  ChangePhoneNumberRequest,
  GetUserIdRequest,
  GetUserIdResponse,
  CheckAuthPhoneNumberRequest,
  CheckAuthPhoneNumberResponse,
} from "./dto/UserProfile";
import { ErrorResponse } from './dto/Error';
import { AccountPayable } from "./dto/AccountPayable";

// {} をFormDataに変換する
const toFormData = (req: Record<string, unknown>): FormData => {
  const data = new FormData();
  for (const [k, v] of Object.entries(req)) {
    if (v === null) {
      continue;
    }
    if (v instanceof Blob) {
      data.set(k, v);
    } else if (typeof v !== "object") {
      data.set(k, `${v}`);
    } else {
      // NOTE: FormData の値は string | Blob なので object は stringify() する
      data.set(k, JSON.stringify(v));
    }
  }
  return data;
};

const toUpdateUserRequest = (
  icon: File | null,
  iconUpdateFlag: IconUpdateFlag | null,
  profile: GetUserProfileResponse,
): UpdateUserProfileRequest => {
  return {
    icon,
    iconUpdateFlag,
    nickname: profile.nickname,
    selfIntroduction: profile.selfIntroduction,
    skills: profile.skills,
    twitterUsername: profile.twitterUsername,
    githubUsername: profile.githubUsername,
    websiteUrl: profile.websiteUrl,
    publishedMailAddress: profile.publishedMailAddress,
    affiliation: profile.affiliation,
    affiliationUrl: profile.affiliationUrl,
    codeTheme: profile.codeTheme,
    offerMail: profile.offerMail,
  };
};

/**
 * ユーザー更新API
 * @param icon アイコン
 * @param iconUpdateFlag アイコン更新フラグ
 * @param profile 更新するユーザー情報
 * @returns
 */
export const updateUserProfile = async (
  icon: File | null,
  iconUpdateFlag: IconUpdateFlag | null,
  profile: GetUserProfileResponse,
): Promise<UpdateUserProfileResponse> => {
  const req = toUpdateUserRequest(icon, iconUpdateFlag, profile);
  const data = toFormData(req as unknown as Record<string, unknown>);
  const res = await axios.post<UpdateUserProfileResponse>("/api/user", data, {
    headers: { "Content-Type": "multipart/form-data", },
  });
  return res.data;
};

/**
 * ユーザー更新（法人）API
 * @param profile 更新するユーザー情報
 * @returns
 */
export const updateUserProfileCorporate = async (
  profile: UpdateCorporateUserProfileRequest,
): Promise<UpdateUserProfileResponse> => {
  const res = await axios.put<UpdateUserProfileResponse>("/api/user/corporate", profile, {
    headers: { "Content-Type": "application/json" },
  });
  return res.data;
};

/**
 * 自分のユーザー情報を取得する
 */
export const getUserProfile = async (): Promise<GetUserProfileResponse> => {
  return axios.get<GetUserProfileResponse>(`/api/user`).then((res) => res.data);
};

/**
 * 他人のユーザー情報を取得する
 * @param userId ユーザーID
 * @returns
 */
export const getUsers = async (userId: string): Promise<GetUsersResponse> => {
  return axios
    .get<GetUsersResponse>(`/api/users?id=${userId}`)
    .then((res) => res.data);
};

/**
 * 自ユーザーを削除する
 *
 * NOTE: 取引中の案件があるときは 403 エラーが返ってくる
 */
export const deleteUser = async (): Promise<void> => {
  await axios
    .delete<void>(`/api/user`)
    .catch(e => {
      if (axios.isAxiosError(e)) {
        throw e.response?.data as ErrorResponse;
      }
    });
}

/**
 * 自ユーザーを削除する(法人)
 */
export const deleteCorporateUser = async (): Promise<void> => {
  await axios
    .delete<void>(`/api/user/corporate`)
    .catch(e => {
      if (axios.isAxiosError(e)) {
        throw e.response?.data as ErrorResponse;
      }
    });
}

/**
 * お振込み情報を更新する
 */
export const updatePaymentInformation = async (accountPayable: AccountPayable): Promise<void> => {
  await axios
    .post<AccountPayable>(`/api/user/bank`, accountPayable)
    .catch(e => {
      if (axios.isAxiosError(e)) {
        throw e.response?.data as ErrorResponse;
      }
    });
}

/**
 * 決済情報を削除する
 */
export const deletePaymentInformation = async (): Promise<void> => {
  await axios.delete<void>(`/api/user/card`)
}

/**
 * 決済情報を登録する
 */
export const createPaymentInfomation = async(req: CreatePaymentInformationRequest): Promise<void> => {
  const res = await axios.post(
    `/api/user/card`,
    req,
    {
      headers: { "Content-Type": "application/json" },
    }
  );
  return res.data;
}

/**
 * パスワードを変更する
 */
export const changePassword = async(req: ChangePasswordRequest): Promise<void> => {
  const res = await axios.post(
    `/api/user/password/change`,
    req,
    {
      headers: { "Content-Type": "application/json" },
    }
  );
  return res.data;
}

/**
 * メールアドレスを変更する
 */
export const changeMailAddress = async(req: ChangeMailAddressRequest): Promise<void> => {
  const res = await axios.post(
    `/api/user/mail/change`,
    req,
    {
      headers: { "Content-Type": "application/json" },
    }
  );
  return res.data;
}

/**
 * 電話番号を登録する
 */
export const registerPhoneNumber = async(req:RegisterPhoneNumberRequest): Promise<void> => {
  const res = await axios.post(
    `/api/user/phoneNumber/registration`,
     req,  
    {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
}

/**
 * 電話番号を取得する
 */

export const getPhoneNumber = async(req:GetAuthPhoneNumberRequest): Promise<string> => {
  const res = await axios.post<string>(
    `/api/user/phoneNumber`,
    req,
    {
      headers: { "Content-Type": "application/json" },
    }
    );
    
    return res.data;
}

/**
 * 認証コードの発行
*/
export const createAuthCode = async (req: SendAuthCode): Promise<void> => {
  const res = await axios
  .post<void>(`/api/user/send/authCode`,
      req
      , { headers: { "Content-Type": "application/json" } }
      );
      return res.data;
    }

/**
 * 認証コードの発行（メール）
*/
export const createMailAuthCode = async (req: SendMailAuthCode): Promise<void> => {
  const res = await axios
  .post<void>(`/api/user/send/mailAuthCode`,
      req
      , { headers: { "Content-Type": "application/json" } }
      );
      return res.data;
    }
    

/**
 * 電話番号を変更する
 */
export const changePhoneNumber = async (req: ChangePhoneNumberRequest): Promise<void> => {
  const res = await axios.post(
    `/api/user/phone/change`,
    req,
    {
      headers: { "Content-Type": "application/json" },
    }
  );
  return res.data;
}

/**
 * 電話番号の重複チェック
 */
export const checkDuplicatePhoneNumber = async (req:CheckAuthPhoneNumberRequest): Promise<CheckAuthPhoneNumberResponse> => {
  const res = await axios.post(
    `/api/user/phone/check`,
    req,
    {
      headers: {"Content-Type":"application/json"},
    }
  );
  return res.data;
}



/**
 * アカウントIDからユーザーIDを取得する
 */
export const getUserIdFromId = async (req: GetUserIdRequest): Promise<GetUserIdResponse> => {
  const res = await axios.post<GetUserIdResponse>(
    `/api/user/get/userId`,
    req,
    {
      headers: { "Content-Type": "application/json" },
    }
  );
  return res.data;
}
/**
 * 二要素認証の設定の切り替え
 */
export const updateTwoFactor = async (userId: string): Promise<boolean> => {
  const res = await axios.post<boolean>(
    `/api/user/twoFactor/settings?userId=${userId}`,
    userId,
    {
      headers: { "Content-Type": "application/json" },
    }
  );
  return res.data;
}